
import { supabase } from '@/lib/supabase';

export type CloudflareTimeRange = '1d' | '7d' | '30d' | '60d';

export interface CloudflareMetricSeries {
  all: number;
  data: { date: string; value: number }[];
}

export interface CloudflareTrafficStats {
  pageViews: CloudflareMetricSeries;
  visits: CloudflareMetricSeries;
  uniqueVisitors: CloudflareMetricSeries;
  requests: CloudflareMetricSeries;
  topCountries: {
    country: string;
    requests: number;
    pageViews: number;
  }[];
  window?: {
    startDate: string;
    endDate: string;
    period: CloudflareTimeRange;
  };
}

type CloudflareTimeseriesEntry = {
  since?: string;
  date?: string;
  pageViews?: number;
  visits?: number;
  uniqueVisitors?: number;
  requests?: {
    all?: number;
  };
};

type CloudflareCountryEntry = {
  country?: string;
  requests?: number;
  pageViews?: number;
};

type CloudflareEdgeResponse = {
  success?: boolean;
  error?: string;
  window?: {
    startDate?: string;
    endDate?: string;
    period?: CloudflareTimeRange;
  };
  traffic?: {
    timeseries?: CloudflareTimeseriesEntry[];
    totals?: {
      pageViews?: { all?: number };
      visits?: { all?: number };
      uniqueVisitors?: { all?: number };
      requests?: { all?: number; infrastructure?: boolean };
    };
  };
  topCountries?: CloudflareCountryEntry[];
};

function resolveEntryDate(entry: CloudflareTimeseriesEntry): string {
  const raw = entry.date || entry.since;
  if (!raw) return '';
  return new Date(raw).toISOString().split('T')[0];
}

function mapSeries(
  timeseries: CloudflareTimeseriesEntry[],
  pickValue: (entry: CloudflareTimeseriesEntry) => number,
  totalFallback = 0,
): CloudflareMetricSeries {
  const data = timeseries
    .map((entry) => ({
      date: resolveEntryDate(entry),
      value: pickValue(entry),
    }))
    .filter((entry) => entry.date);

  return {
    all: data.reduce((sum, entry) => sum + entry.value, 0) || totalFallback,
    data,
  };
}

export async function getCloudflareTrafficStats(period: CloudflareTimeRange): Promise<CloudflareTrafficStats> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

    if (!supabaseUrl || !anonKey) {
      throw new Error('Supabase environment is not configured');
    }

    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session?.access_token) {
      throw new Error('You must be signed in to view Cloudflare analytics');
    }

    const url = `${supabaseUrl}/functions/v1/cloudflare-analytics?period=${encodeURIComponent(period)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'apikey': anonKey,
        'Authorization': `Bearer ${session.access_token}`,
      },
    });

    if (!response.ok) {
      console.error('Cloudflare analytics edge function returned status:', response.status);
      throw new Error(`Cloudflare analytics is unavailable (${response.status}).`);
    }

    const data = (await response.json()) as CloudflareEdgeResponse;

    if (!data || !data.success) {
      const errorDetails = data?.error || 'Unknown error';
      console.error('Cloudflare API Error:', errorDetails);
      throw new Error('Cloudflare analytics is unavailable.');
    }

    const traffic = data.traffic || { timeseries: [], totals: {} };
    const timeseries = Array.isArray(traffic.timeseries) ? traffic.timeseries : [];
    const totals = traffic.totals || {};
    const topCountries = data.topCountries || [];

    const formattedTopCountries = topCountries.map((country) => ({
      country: country.country || 'Unknown',
      requests: country.requests || 0,
      pageViews: country.pageViews || 0,
    }));

    return {
      pageViews: mapSeries(timeseries, (entry) => entry.pageViews || 0, totals.pageViews?.all || 0),
      visits: mapSeries(timeseries, (entry) => entry.visits || 0, totals.visits?.all || 0),
      uniqueVisitors: mapSeries(
        timeseries,
        (entry) => entry.uniqueVisitors || 0,
        totals.uniqueVisitors?.all || 0,
      ),
      requests: mapSeries(
        timeseries,
        (entry) => entry.requests?.all || 0,
        totals.requests?.all || 0,
      ),
      topCountries: formattedTopCountries,
      window: data.window?.startDate && data.window?.endDate && data.window?.period
        ? {
            startDate: data.window.startDate,
            endDate: data.window.endDate,
            period: data.window.period,
          }
        : undefined,
    };
  } catch (error) {
    console.error('Error in getCloudflareTrafficStats:', error);
    throw error;
  }
}
